import React from 'react';
import { GoalMainCategory } from '../Milestone_organizer/types';

interface GoalCategorySelectorProps {
  onSelect: (category: GoalMainCategory) => void;
  onCancel: () => void;
}

const CATEGORIES: {
  id: GoalMainCategory;
  label: string;
  description: string;
  examples: string[];
  borderColor: string;
  hoverBg: string;
  accentColor: string;
}[] = [
  {
    id: 'personal',
    label: 'Persoonlijk',
    description: 'Groei, gezondheid en persoonlijke ontwikkeling',
    examples: ['Gezondheid & vitaliteit', 'Leren & opleiding', 'Mindset & welzijn', 'Financieel persoonlijk'],
    borderColor: '#FECDD3',
    hoverBg: '#FFF1F2',
    accentColor: '#BE123C',
  },
  {
    id: 'business',
    label: 'Zakelijk',
    description: 'Ondernemersambities en bedrijfsdoelstellingen',
    examples: ['Bedrijf starten', 'Schalen of uitbreiden', 'Product of dienst lanceren', 'Bedrijf verkopen / exit'],
    borderColor: '#FDE68A',
    hoverBg: '#FFFBEB',
    accentColor: '#92400E',
  },
  {
    id: 'team',
    label: 'Samen',
    description: 'Gedeelde doelen met een team of partner',
    examples: ['Team project', 'Samenwerking', 'Strategisch partnership', 'Gezamenlijk initiatief'],
    borderColor: '#99F6E4',
    hoverBg: '#F0FDFA',
    accentColor: '#0F766E',
  },
];

/**
 * GoalCategorySelector - Eerste stap bij het aanmaken van een doel.
 * Kies het type: Persoonlijk, Zakelijk of Samen.
 */
const GoalCategorySelector: React.FC<GoalCategorySelectorProps> = ({ onSelect, onCancel }) => {
  return (
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Wat voor soort doel wil je stellen?
        </h2>
        <p className="text-sm text-gray-500">
          Kies het type dat het beste past. Dit helpt ons de juiste vragen te stellen.
        </p>
      </div>

      <div className="space-y-3">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => onSelect(cat.id)}
            className="w-full text-left rounded-xl border-2 p-5 transition-all group"
            style={{ borderColor: cat.borderColor, backgroundColor: 'white' }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = cat.hoverBg;
              e.currentTarget.style.borderColor = cat.accentColor;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = cat.borderColor;
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-base font-semibold text-gray-900 mb-1">
                  {cat.label}
                </p>
                <p className="text-sm text-gray-500 mb-3">
                  {cat.description}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {cat.examples.map(ex => (
                    <span
                      key={ex}
                      className="text-xs px-2 py-0.5 rounded-full border"
                      style={{ borderColor: cat.borderColor, color: cat.accentColor, backgroundColor: cat.hoverBg }}
                    >
                      {ex}
                    </span>
                  ))}
                </div>
              </div>
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 transition-colors"
                style={{ backgroundColor: cat.hoverBg }}
              >
                <svg
                  width="16" height="16" viewBox="0 0 16 16" fill="none"
                  style={{ color: cat.accentColor }}
                >
                  <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6 text-center">
        <button
          onClick={onCancel}
          className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          Annuleren
        </button>
      </div>
    </div>
  );
};

export default GoalCategorySelector;
